/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useCallback } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { Box, Grid, Typography, useTheme } from '@mui/material'
import { get } from 'lodash'
import EventInstanceTypeRadioGroup from 'components/configured-service/common/EventInstanceTypeRadioGroup'
import { HHFormSelectField, HHFormNumberField } from 'components/form-fields/v5'
import { EVENT_INSTANCE_TYPES } from 'settings/constants/service'
import { ALL_DAYS, WEEK_OF_MONTH_IN_NUMBERS } from 'settings/constants/day'
import T from 'T'
import { HHAlert } from 'components/common/HHAlert'
import { Z_INDEX } from 'theme/zIndex'
import { validateNonWeeklyRecurrence } from 'components/configured-service/EditServiceRecurrenceDialog/utils/validationHelpers'

const CreateServiceRecurrenceNonWeeklySelector = () => {
  const {
    control,
    watch,
    trigger,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext()
  const theme = useTheme()

  const eventInstanceType = watch('eventInstanceType')
  const recurrenceFrequency = watch('recurrenceFrequency')
  const eventOccurrence = watch('eventOccurrence')
  const dayOfTheWeek = watch('dayOfTheWeek')
  const dayOfTheMonth = watch('dayOfTheMonth')

  // Auto-population effect on mount - specific to Create Flow
  useEffect(() => {
    // Only run if we have a frequency (should always be there default 1)
    const currentFreq = parseInt(recurrenceFrequency, 10) || 1

    // Check if type is set, if not default to DAY_OF_WEEK
    let currentType = getValues('eventInstanceType')
    if (!currentType) {
      setValue('eventInstanceType', EVENT_INSTANCE_TYPES.DAY_OF_WEEK)
      currentType = EVENT_INSTANCE_TYPES.DAY_OF_WEEK
    }

    if (currentType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK) {
      const currentOcc = getValues('eventOccurrence')
      const currentDow = getValues('dayOfTheWeek')

      // Helper to check for validity (not empty/undefined)
      const hasValidValues = val => {
        if (Array.isArray(val)) return val.some(v => v !== undefined && v !== null && v !== '')
        return val !== undefined && val !== null && val !== ''
      }

      // Note: WEEK_OF_MONTH_IN_NUMBERS key 1 is value 1
      const defaultOccValue = 1
      const defaultDowValue = ALL_DAYS[0] // Monday

      if (!hasValidValues(currentOcc)) {
        // Set as array matching frequency.
        const defaultOcc = Array(currentFreq).fill(defaultOccValue)
        setValue('eventOccurrence', defaultOcc)
      }

      if (!hasValidValues(currentDow)) {
        const defaultDow = Array(currentFreq).fill(defaultDowValue)
        setValue('dayOfTheWeek', defaultDow)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isDayOfWeekType = eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK
  const isSpecificDateType = eventInstanceType === EVENT_INSTANCE_TYPES.SPECIFIC_DATE

  // Use the actual array length based on the event instance type
  const numberOfOccurrences = (() => {
    if (isDayOfWeekType && Array.isArray(dayOfTheWeek)) {
      return dayOfTheWeek.length
    }
    if (isSpecificDateType && Array.isArray(dayOfTheMonth)) {
      return dayOfTheMonth.length
    }
    return parseInt(recurrenceFrequency, 10) || 1
  })()

  const validationError = errors?.eventInstanceType?.message

  const getErrorMessage = (fieldBase, index) => {
    return get(errors, `${fieldBase}[${index}].message`)
  }
  const getDayOptionLabel = useCallback(l => l, [])
  const getDayOptionValue = useCallback(v => v, [])
  const getWeekOptionLabel = useCallback(({ value }) => value, [])
  const getWeekOptionValue = useCallback(({ key }) => key, [])

  const validateEventInstanceType = value => {
    const validation = validateNonWeeklyRecurrence(
      dayOfTheMonth,
      dayOfTheWeek,
      eventOccurrence,
      parseInt(recurrenceFrequency, 10) || '',
      value
    )
    return validation.isValid ? true : validation.message
  }

  // Create stable keys for change detection
  const dayOfTheMonthKey = Array.isArray(dayOfTheMonth) ? dayOfTheMonth.join('|') : dayOfTheMonth || ''
  const dayOfTheWeekKey = Array.isArray(dayOfTheWeek) ? dayOfTheWeek.join('|') : dayOfTheWeek || ''
  const eventOccurrenceKey = Array.isArray(eventOccurrence) ? eventOccurrence.join('|') : eventOccurrence || ''

  const handleValidationTrigger = useCallback(() => {
    if (eventInstanceType && recurrenceFrequency) {
      trigger(['eventInstanceType', 'eventOccurrence', 'dayOfTheWeek', 'dayOfTheMonth'])
    }
  }, [eventInstanceType, recurrenceFrequency, trigger])

  const SelectProps = {
    MenuProps: {
      sx: {
        zIndex: Z_INDEX.MENU(theme),
      },
    },
  }

  useEffect(() => {
    handleValidationTrigger()
  }, [recurrenceFrequency, eventInstanceType, eventOccurrenceKey, dayOfTheWeekKey, dayOfTheMonthKey, handleValidationTrigger])

  return (
    <Box>
      {validationError && <HHAlert severity="error">{validationError}</HHAlert>}

      <Grid container size={12} mb={2}>
        <Controller
          rules={{
            required: T.EVENT_INSTANCE_REQUIRED,
            validate: validateEventInstanceType,
          }}
          control={control}
          name="eventInstanceType"
          render={({ field }) => <EventInstanceTypeRadioGroup {...field} showError={false} />}
        />
      </Grid>

      {numberOfOccurrences > 1 && (
        <Grid container size={12} mb={2}>
          <Typography variant="body2" color="text.secondary">
            Configure {numberOfOccurrences} occurrences per interval:
          </Typography>
        </Grid>
      )}

      {isDayOfWeekType && (
        <Box>
          {Array.from({ length: numberOfOccurrences }, (_, index) => (
            <Grid key={index} container spacing={2} mb={2}>
              {numberOfOccurrences > 1 && (
                <Grid size={12}>
                  <Typography variant="body2" fontWeight="medium" color="text.primary">
                    {T.EVENT_OCCURRENCE} {index + 1}
                  </Typography>
                </Grid>
              )}
              <Grid size={6}>
                <HHFormSelectField
                  rules={{
                    required: T.THIS_FIELD_IS_REQUIRED,
                  }}
                  SelectProps={SelectProps}
                  control={control}
                  deprecatedLabel={false}
                  error={Boolean(getErrorMessage('eventOccurrence', index))}
                  fullWidth
                  getOptionLabel={getWeekOptionLabel}
                  getOptionValue={getWeekOptionValue}
                  helperText={getErrorMessage('eventOccurrence', index)}
                  label={T.EVENT_OCCURRENCE}
                  name={`eventOccurrence[${index}]`}
                  options={WEEK_OF_MONTH_IN_NUMBERS}
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <HHFormSelectField
                  rules={{
                    required: T.THIS_FIELD_IS_REQUIRED,
                  }}
                  SelectProps={SelectProps}
                  control={control}
                  deprecatedLabel={false}
                  error={Boolean(getErrorMessage('dayOfTheWeek', index))}
                  fullWidth
                  getOptionLabel={getDayOptionLabel}
                  getOptionValue={getDayOptionValue}
                  helperText={getErrorMessage('dayOfTheWeek', index)}
                  label={T.DAY_OF_THE_WEEK}
                  name={`dayOfTheWeek[${index}]`}
                  options={ALL_DAYS}
                  size="small"
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      )}

      {isSpecificDateType && (
        <Box>
          {Array.from({ length: numberOfOccurrences }, (_, index) => (
            <Grid container spacing={2} mb={2} key={index}>
              {numberOfOccurrences > 1 && (
                <Grid size={12}>
                  <Typography variant="body2" fontWeight="medium" color="text.primary">
                    {T.EVENT_OCCURRENCE} {index + 1}
                  </Typography>
                </Grid>
              )}
              <Grid size={12}>
                <HHFormNumberField
                  rules={{
                    required: T.THIS_FIELD_IS_REQUIRED,
                    min: {
                      value: 1,
                      message: T.DAY_FIELD_VALIDATION_ERROR,
                    },
                    max: {
                      value: 31,
                      message: T.DAY_FIELD_VALIDATION_ERROR,
                    },
                  }}
                  SelectProps={SelectProps}
                  control={control}
                  deprecatedLabel={false}
                  error={Boolean(getErrorMessage('dayOfTheMonth', index))}
                  fullWidth
                  helperText={getErrorMessage('dayOfTheMonth', index)}
                  label={T.DAY_OF_THE_MONTH}
                  name={`dayOfTheMonth[${index}]`}
                  showErrorMessage
                  size="small"
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default CreateServiceRecurrenceNonWeeklySelector
